import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLogin } from '../../hooks/useLogin';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FormErrorBanner } from '../../components/shared/FormErrorBanner';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { email, setEmail, password, setPassword, fieldErrors, formErrors, handleSubmit, loading } = useLogin();

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
  const labelClass = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";

  const renderFieldError = (errors: string[]) => {
    if (errors.length === 0) {
      return null;
    }

    return <p className="mt-2 text-sm text-destructive">{t(errors[0])}</p>;
  };

  return (
    <div className="max-w-md mx-auto p-6 mt-10">
      <Card>
        <CardHeader>
          <h3 className="text-2xl leading-none tracking-tight">{t('auth.login.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('auth.login.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <FormErrorBanner errors={formErrors} className="mb-4" />

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className={labelClass}>{t('auth.login.email_label')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder={t('auth.login.email_placeholder')}
                required
                autoFocus
              />
              {renderFieldError(fieldErrors.email)}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelClass.replace('mb-2', '')}>{t('auth.login.password_label')}</label>
                <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-foreground font-medium">
                  {t('auth.login.forgot_password')}
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder={t('auth.login.password_placeholder')}
                required
              />
              {renderFieldError(fieldErrors.password)}
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? t('auth.login.logging_in') : t('auth.login.submit')}
            </Button>

            <div className="text-center text-sm mt-4">
              <span className="text-muted-foreground">{t('auth.login.no_account')} </span>
              <Link to="/register" className="text-foreground underline hover:no-underline font-medium">
                {t('auth.registration.title')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
